/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Row,
  Col,
  Container,
  Modal,
  ModalFooter,
  ModalHeader,
  ModalBody,
  Label,
  Spinner,
} from 'reactstrap';
import PageBreadcrumb from '../../Components/Common/PageBreadcrumb';
import DataTable from 'react-data-table-component';
import ActionButton from '../../Components/Common/ActionButton';
import DeleteModal from '../../Components/Common/DeleteModal';
import ListPager from '../../Components/Common/ListPager';
import {
  ActiveFilterChips,
  ClearFiltersButton,
  ListFilterSelect,
  ListSearch,
} from '../../Components/Common/ListFilters';
import { EmptyState, SkeletonRows } from '../../Components/Common/ListStates';
import notify from '../../utils/notify';
import '../TenderOpening/components/TenderOpening.css';
import axios from 'axios';
import { api } from '../../config';
import { getAuthToken } from '../../auth/tokenvalidation';
import {
  CheckField,
  FormSection,
  RequiredNote,
  SelectField,
  TextAreaField,
  TextField,
  focusFirstError,
  hasErrors,
  notBelow,
  required,
  validateForm,
} from '../../Components/Form';
import StatusBadge from '../../Components/Common/StatusBadge';

const JETTY_SCHEMA = {
  locationId: { label: 'Location', rules: [required()] },
  name: { label: 'Jenis Sarana Tambat', rules: [required()] },
  productIds: {
    label: 'Products',
    rules: [required('Select at least one product')],
  },
  draftMax: { label: 'Draft max', rules: [notBelow('draftMin', 'draft min')] },
  loaMax: { label: 'LOA max', rules: [notBelow('loaMin', 'LOA min')] },
  dwtMax: { label: 'DWT max', rules: [notBelow('dwtMin', 'DWT min')] },
};
const JETTY_ORDER = [
  'locationId',
  'name',
  'productIds',
  'draftMax',
  'loaMax',
  'dwtMax',
];

const JETTY_STATUS_OPTIONS = [
  { value: 'true', label: 'Active' },
  { value: 'false', label: 'Inactive' },
];

const EMPTY_JETTY_FORM = {
  id: null,
  locationId: '',
  name: '',
  description: '',
  productIds: [],
  draftMin: '',
  draftMax: '',
  loaMin: '',
  loaMax: '',
  dwtMin: '',
  dwtMax: '',
  isActive: true,
};

const JettyLocationList = () => {
  const token = getAuthToken();

  const [data, setData] = useState({ items: [] });
  const [totalRows, setTotalRows] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filterText, setFilterText] = useState('');
  const [search, setSearch] = useState('');
  const requestRef = useRef(0);
  const [selectedLocation, setSelectedLocation] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [loading, setLoading] = useState(false);
  const startEntry = totalRows === 0 ? 0 : (currentPage - 1) * rowsPerPage + 1;
  const endEntry = Math.min(currentPage * rowsPerPage, totalRows);

  const [modalJetty, setModalJetty] = useState(false);
  const [jettyFormData, setJettyFormData] = useState(EMPTY_JETTY_FORM);
  const [jettyErrors, setJettyErrors] = useState({});
  const [jettySaving, setJettySaving] = useState(false);
  const [productOptions, setProductOptions] = useState([]);
  const [locationOptions, setLocationOptions] = useState([]);
  const [deleteModal, setDeleteModal] = useState(false);
  const [invoice, setInvoice] = useState(null);

  // Debounce typing so each keystroke is not a request.
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(filterText.trim());
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [filterText]);

  useEffect(() => {
    fetchLocations(token);
    fetchProducts(token);
  }, [token]);

  useEffect(() => {
    fetchData(currentPage, rowsPerPage, token, search);
  }, [
    currentPage,
    rowsPerPage,
    token,
    search,
    selectedLocation,
    selectedStatus,
  ]);

  const handleFilterChange = e => {
    setFilterText(e.target.value);
    // Clearing applies at once; typing waits for the debounce.
    if (!e.target.value) {
      setSearch('');
      setCurrentPage(1);
    }
  };

  const handleLocationFilterChange = value => {
    setSelectedLocation(value);
    setCurrentPage(1);
  };

  const handleStatusFilterChange = value => {
    setSelectedStatus(value);
    setCurrentPage(1);
  };

  const clearFilter = () => {
    setFilterText('');
    setSearch('');
    setSelectedLocation('');
    setSelectedStatus('');
    setCurrentPage(1);
  };

  const fetchData = async (currentPage, rowsPerPage, token, searchText) => {
    const reqId = ++requestRef.current;
    setLoading(true);
    try {
      const queryParams = new URLSearchParams({
        PageNumber: currentPage.toString(),
        PageSize: rowsPerPage.toString(),
      });
      if (searchText) {
        queryParams.append('Search', searchText);
      }
      if (selectedLocation) {
        queryParams.append('LocationId', selectedLocation);
      }
      if (selectedStatus) {
        queryParams.append('IsActive', selectedStatus);
      }

      const response = await fetch(
        `${api.API_URL}/location-jetty?${queryParams.toString()}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          credentials: 'same-origin',
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (reqId !== requestRef.current) return; // stale response

      const items =
        result?.data?.items || result?.items || result?.data || result || [];
      const totalItems =
        result?.data?.totalItems || result?.totalItems || items.length;

      setData({
        items: Array.isArray(items) ? items : [],
      });
      setTotalRows(totalItems);
    } catch (error) {
      if (reqId !== requestRef.current) return;
      notify.apiError(error, 'load data');
      setData({ items: [] });
      setTotalRows(0);
    } finally {
      if (reqId === requestRef.current) setLoading(false);
    }
  };

  const fetchLocations = async token => {
    try {
      const queryParams = new URLSearchParams({
        PageNumber: 1,
        PageSize: 300,
        SortBy: 'Name',
        IsDescending: false,
      });

      const response = await fetch(
        `${api.API_URL}/location?${queryParams.toString()}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          credentials: 'same-origin',
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      const items =
        result?.data?.items || result?.items || result?.data || result || [];
      setLocationOptions(
        (Array.isArray(items) ? items : []).map(item => ({
          value: item.id,
          label: item.name,
        }))
      );
    } catch (error) {
      notify.apiError(error, 'load locations');
      setLocationOptions([]);
    }
  };

  const fetchProducts = async token => {
    try {
      const response = await fetch(`${api.API_URL}/product`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        credentials: 'same-origin',
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      const items =
        result?.data?.items || result?.items || result?.data || result || [];
      setProductOptions(
        (Array.isArray(items) ? items : []).map(item => ({
          value: item.id,
          label: item.name,
        }))
      );
    } catch (error) {
      notify.apiError(error, 'load products');
      setProductOptions([]);
    }
  };

  const fetchJettyDetail = async id => {
    try {
      const response = await fetch(`${api.API_URL}/location-jetty/${id}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        credentials: 'same-origin',
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      const detail = result?.data ?? result;
      setJettyFormData({
        id: detail.id ?? id,
        locationId: detail.locationId || '',
        name: detail.name || '',
        description: detail.description || '',
        productIds: (detail.products || []).map(product => product.id),
        draftMin: detail.draftMin ?? '',
        draftMax: detail.draftMax ?? '',
        loaMin: detail.loaMin ?? '',
        loaMax: detail.loaMax ?? '',
        dwtMin: detail.dwtMin ?? '',
        dwtMax: detail.dwtMax ?? '',
        isActive: detail.isActive ?? true,
      });
    } catch (error) {
      notify.apiError(error, 'fetch jetty details');
    }
  };

  const toNumberOrNull = value =>
    value === '' || value === null || value === undefined
      ? null
      : Number(value);

  const validateJettyForm = () => {
    const nextErrors = validateForm(jettyFormData, JETTY_SCHEMA);
    setJettyErrors(nextErrors);
    if (hasErrors(nextErrors)) focusFirstError(nextErrors, JETTY_ORDER);
    return !hasErrors(nextErrors);
  };

  // Update one field and clear its error as soon as the user edits it.
  const setJettyField = (field, value) => {
    setJettyFormData(prev => ({ ...prev, [field]: value }));
    setJettyErrors(prev => ({ ...prev, [field]: '' }));
  };

  const handleNewModal = () => {
    setJettyFormData(EMPTY_JETTY_FORM);
    setJettyErrors({});
    setModalJetty(true);
  };

  const handleUpdateModal = async id => {
    setJettyFormData(EMPTY_JETTY_FORM);
    setJettyErrors({});
    await fetchJettyDetail(id);
    setModalJetty(true);
  };

  const handleDeleteModal = row => {
    setInvoice(row);
    setDeleteModal(true);
  };

  const confirmDeleteInvoice = async () => {
    if (!invoice) return;
    try {
      const response = await fetch(
        `${api.API_URL}/location-jetty/${invoice.id}`,
        {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message || `HTTP error! status: ${response.status}`
        );
      }

      notify.success('Jenis Sarana Tambat deleted successfully.');
      setDeleteModal(false);
      setInvoice(null);
      fetchData(currentPage, rowsPerPage, token, search);
    } catch (err) {
      notify.apiError(err, 'delete jetty');
    }
  };

  const handleSaveJetty = async () => {
    if (!validateJettyForm()) return;

    const payload = {
      locationId: jettyFormData.locationId,
      name: jettyFormData.name,
      description: jettyFormData.description,
      productIds: jettyFormData.productIds,
      draftMin: toNumberOrNull(jettyFormData.draftMin),
      draftMax: toNumberOrNull(jettyFormData.draftMax),
      loaMin: toNumberOrNull(jettyFormData.loaMin),
      loaMax: toNumberOrNull(jettyFormData.loaMax),
      dwtMin: toNumberOrNull(jettyFormData.dwtMin),
      dwtMax: toNumberOrNull(jettyFormData.dwtMax),
      isActive: jettyFormData.isActive,
    };

    const config = {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    };

    setJettySaving(true);
    try {
      if (jettyFormData.id) {
        await axios.put(
          `${api.API_URL}/location-jetty/${jettyFormData.id}`,
          payload,
          config
        );
        notify.success('Jenis Sarana Tambat updated successfully.');
      } else {
        await axios.post(`${api.API_URL}/location-jetty`, payload, config);
        notify.success('Jenis Sarana Tambat added successfully.');
      }
      setModalJetty(false);
      fetchData(currentPage, rowsPerPage, token, search);
    } catch (error) {
      notify.apiError(error, 'save Jenis Sarana Tambat');
    } finally {
      setJettySaving(false);
    }
  };

  const columns = [
    {
      id: 'locationName',
      name: 'Location Name',
      selector: row => row.locationName,
      sortable: true,
      width: '25%',
      cell: row => (
        <span
          className='to-title'
          title={String((row.locationName || '-') ?? '')}
        >
          {(row.locationName || '-') ?? '-'}
        </span>
      ),
    },

    {
      id: 'name',
      name: 'Jenis Sarana Tambat',
      selector: row => row.name,
      sortable: true,
      width: '25%',
      cell: row => (
        <span className='pv-desc' title={String((row.name || '-') ?? '')}>
          {(row.name || '-') ?? '-'}
        </span>
      ),
    },

    {
      id: 'description',
      name: 'Description',
      selector: row => row.description,
      sortable: true,
      width: '25%',
      cell: row => (
        <span
          className='pv-desc'
          title={String((row.description || '-') ?? '')}
        >
          {(row.description || '-') ?? '-'}
        </span>
      ),
    },

    {
      id: 'isActive',
      name: 'Status',
      selector: row => row.isActive,
      sortable: true,
      width: '10%',
      cell: row => (
        <StatusBadge
          tone={row.isActive ? 'success' : 'muted'}
          label={row.isActive ? 'Active' : 'Inactive'}
        />
      ),
    },

    {
      name: <span className='text-center w-100'>Action</span>,
      width: '15%',
      center: true,
      cell: row => (
        <div className='d-flex gap-1 justify-content-center w-100'>
          <ActionButton
            variant='icon'
            size='sm'
            icon='ri-edit-line'
            label='Edit'
            onClick={() => handleUpdateModal(row.id)}
          />
          <ActionButton
            variant='icon'
            size='sm'
            danger
            icon='ri-delete-bin-line'
            label='Delete'
            confirm={false}
            onClick={() => handleDeleteModal(row)}
          />
        </div>
      ),
    },
  ];

  document.title = 'Data Port Restriction';

  const handlePageChange = useCallback(page => {
    setCurrentPage(page);
  }, []);

  const handlePerRowsChange = useCallback(newPerPage => {
    setRowsPerPage(newPerPage);
    setCurrentPage(1);
  }, []);

  const filterChips = [
    filterText && {
      key: 'search',
      label: 'Search',
      value: filterText,
      onRemove: () => handleFilterChange({ target: { value: '' } }),
    },
    selectedLocation && {
      key: 'location',
      label: 'Location',
      value:
        locationOptions.find(o => o.value === selectedLocation)?.label ??
        selectedLocation,
      onRemove: () => handleLocationFilterChange(''),
    },
    selectedStatus && {
      key: 'status',
      label: 'Status',
      value:
        JETTY_STATUS_OPTIONS.find(o => o.value === selectedStatus)?.label ??
        selectedStatus,
      onRemove: () => handleStatusFilterChange(''),
    },
  ].filter(Boolean);

  return (
    <React.Fragment>
      <div className='page-content'>
        <DeleteModal
          show={deleteModal}
          onDeleteClick={() => confirmDeleteInvoice()}
          title='Delete this jetty?'
          message='It will be permanently deleted. This cannot be undone.'
          subject={invoice?.name}
          confirmLabel='Delete jetty'
          onCloseClick={() => {
            setDeleteModal(false);
            setInvoice(null);
          }}
        />
        <Container fluid>
          <div className='to-page email-wrapper d-lg-flex gap-1 mx-n4 mt-n4 p-1'>
            <div className='email-content minimal-border'>
              <div className='p-4'>
                <div className='to-hero mb-4'>
                  <PageBreadcrumb
                    fallback={{
                      label: 'Port Restriction',
                      to: '/apps-jetty-location-view',
                    }}
                  />

                  <div className='d-flex align-items-center gap-3 flex-wrap'>
                    <span className='to-hero-icon'>
                      <i className='ri-anchor-line' aria-hidden='true' />
                    </span>
                    <div className='flex-grow-1'>
                      <h4 className='to-hero-title'>Port Restriction</h4>
                      <p className='to-hero-subtitle'>
                        Maintain the jetties for each location and the products
                        they handle.
                      </p>
                    </div>
                    <div className='d-flex gap-2 flex-wrap'>
                      <button
                        type='button'
                        className='to-hero-btn'
                        onClick={() =>
                          fetchData(currentPage, rowsPerPage, token, search)
                        }
                      >
                        <i className='ri-refresh-line' aria-hidden='true' />
                        Refresh
                      </button>
                      <button
                        type='button'
                        className='to-hero-btn to-hero-btn--solid'
                        onClick={() => handleNewModal()}
                      >
                        <i className='ri-add-line' aria-hidden='true' />
                        Create New Port Restriction
                      </button>
                    </div>
                  </div>
                </div>

                <div className='to-panel'>
                  <div className='to-toolbar'>
                    <ListSearch
                      value={filterText}
                      onChange={handleFilterChange}
                      placeholder='Search jetty locations...'
                      ariaLabel='Search jetty locations'
                    />
                    <ListFilterSelect
                      label='Location'
                      value={selectedLocation}
                      onChange={handleLocationFilterChange}
                      allLabel='All Locations'
                      options={locationOptions}
                    />
                    <ListFilterSelect
                      label='Status'
                      value={selectedStatus}
                      onChange={handleStatusFilterChange}
                      allLabel='All Status'
                      options={JETTY_STATUS_OPTIONS}
                    />
                    <ClearFiltersButton
                      count={filterChips.length}
                      onClick={clearFilter}
                    />
                    <span className='to-toolbar-meta'>
                      {loading
                        ? 'Loading...'
                        : `Showing ${startEntry}–${endEntry} of ${totalRows} jetty locations`}
                    </span>
                  </div>

                  <ActiveFilterChips chips={filterChips} />

                  <div className='to-table-wrap'>
                    <DataTable
                      columns={columns}
                      data={data.items}
                      highlightOnHover
                      persistTableHead
                      defaultSortFieldId='locationName'
                      defaultSortAsc
                      progressPending={loading}
                      progressComponent={<SkeletonRows />}
                      noDataComponent={
                        <EmptyState
                          title='No Port Restriction found'
                          emptyHint='No Port Restriction have been registered yet. Jetties added here become available for location and delivery details.'
                          filteredHint='No Port Restriction matches the current filters.'
                          hasFilters={filterChips.length > 0}
                          onClear={clearFilter}
                          action={{
                            label: 'Create New Port Restriction',
                            onClick: handleNewModal,
                          }}
                        />
                      }
                      customStyles={{
                        headCells: {
                          style: { paddingLeft: '12px', paddingRight: '12px' },
                        },
                        cells: {
                          style: {
                            paddingLeft: '12px',
                            paddingRight: '12px',
                            wordBreak: 'break-word',
                          },
                        },
                        table: { style: { width: '100%' } },
                      }}
                    />
                  </div>

                  <ListPager
                    currentPage={currentPage}
                    rowsPerPage={rowsPerPage}
                    totalRows={totalRows}
                    isLoading={loading}
                    onPageChange={handlePageChange}
                    onRowsPerPageChange={handlePerRowsChange}
                  />
                </div>
              </div>
            </div>
          </div>

          <Modal
            isOpen={modalJetty}
            role='dialog'
            autoFocus={true}
            centered
            id='addJettyModal'
            toggle={() => setModalJetty(!modalJetty)}
            data-bs-backdrop='static'
            size='lg'
            zIndex={1060}
          >
            <ModalHeader
              className='bg-soft-info'
              toggle={() => setModalJetty(false)}
            >
              <span className='modal-title text-info fw-bold'>
                {jettyFormData.id
                  ? 'Edit Jenis Sarana Tambat'
                  : 'Add Jenis Sarana Tambat'}
              </span>
            </ModalHeader>

            <ModalBody>
              <RequiredNote />
              <FormSection
                title='Jenis Sarana Tambat'
                description='Where the jetty is and what it handles'
                icon='ri-anchor-line'
              >
                <SelectField
                  label='Location'
                  name='locationId'
                  required
                  placeholder='Select location'
                  options={locationOptions}
                  value={jettyFormData.locationId}
                  error={jettyErrors.locationId}
                  onChange={value => setJettyField('locationId', value ?? '')}
                />
                <TextField
                  label='Jenis Sarana Tambat'
                  name='name'
                  required
                  value={jettyFormData.name}
                  error={jettyErrors.name}
                  onChange={e => setJettyField('name', e.target.value)}
                />
                <SelectField
                  span='full'
                  multi
                  label='Products'
                  name='productIds'
                  required
                  placeholder='Select one or more products'
                  options={productOptions}
                  value={jettyFormData.productIds}
                  error={jettyErrors.productIds}
                  onChange={value => setJettyField('productIds', value)}
                />
                <TextAreaField
                  span='full'
                  label='Description'
                  name='description'
                  value={jettyFormData.description}
                  onChange={e => setJettyField('description', e.target.value)}
                />
              </FormSection>

              <FormSection
                title='Vessel limits'
                description='Largest and smallest vessel this jetty accepts. Leave empty for no limit.'
                icon='ri-ship-line'
              >
                <TextField
                  label='Draft min'
                  name='draftMin'
                  type='number'
                  suffix='m'
                  value={jettyFormData.draftMin}
                  onChange={e => setJettyField('draftMin', e.target.value)}
                />
                <TextField
                  label='Draft max'
                  name='draftMax'
                  type='number'
                  suffix='m'
                  value={jettyFormData.draftMax}
                  error={jettyErrors.draftMax}
                  onChange={e => setJettyField('draftMax', e.target.value)}
                />
                <TextField
                  label='LOA min'
                  name='loaMin'
                  type='number'
                  suffix='m'
                  value={jettyFormData.loaMin}
                  onChange={e => setJettyField('loaMin', e.target.value)}
                />
                <TextField
                  label='LOA max'
                  name='loaMax'
                  type='number'
                  suffix='m'
                  value={jettyFormData.loaMax}
                  error={jettyErrors.loaMax}
                  onChange={e => setJettyField('loaMax', e.target.value)}
                />
                <TextField
                  label='DWT min'
                  name='dwtMin'
                  type='number'
                  suffix='MT'
                  value={jettyFormData.dwtMin}
                  onChange={e => setJettyField('dwtMin', e.target.value)}
                />
                <TextField
                  label='DWT max'
                  name='dwtMax'
                  type='number'
                  suffix='MT'
                  value={jettyFormData.dwtMax}
                  error={jettyErrors.dwtMax}
                  onChange={e => setJettyField('dwtMax', e.target.value)}
                />
              </FormSection>

              <FormSection
                title='Status'
                icon='ri-toggle-line'
                columns={1}
                className='mb-0'
              >
                <CheckField
                  switchStyle
                  label='Active'
                  name='isActive'
                  checked={jettyFormData.isActive}
                  onChange={e => setJettyField('isActive', e.target.checked)}
                />
              </FormSection>
            </ModalBody>

            <ModalFooter>
              <ActionButton
                variant='secondary'
                size='sm'
                icon='ri-close-line'
                onClick={() => setModalJetty(false)}
              >
                Closed
              </ActionButton>
              <ActionButton
                variant='primary'
                size='sm'
                icon='ri-save-line'
                loading={jettySaving}
                onClick={handleSaveJetty}
              >
                Save
              </ActionButton>
            </ModalFooter>
          </Modal>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default JettyLocationList;
